import { useState, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  X,
  Award,
  FileText,
  Target,
  AlertTriangle,
  TrendingUp,
  BarChart4,
  CheckCircle,
  Lock,
  MessageSquare,
  ArrowRight,
  Sparkles,
  Leaf,
  Loader2,
  FolderOpen,
  Clock,
  RotateCcw,
  NotebookPen,
  CalendarPlus
} from 'lucide-react'
import { motion } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/providers/AuthProvider'
import { reviewService, type DiscussionPoint } from '@/services/api/reviewService'
import toast from 'react-hot-toast'
import { RecordedByHrNote } from '@/components/performance/review/RecordedByHrNote'
import { DiscussionItems } from '@/components/performance/review/DiscussionItems'
import { discussionPointKeys } from '@/lib/discussionPointKeys'
import { useJourneyViewMode } from '@/lib/journeyViewMode'
import { ViewModeToggle } from '@/components/journey/ViewModeToggle'

// Import modular components
import EvidencePortfolio from '../EvidencePortfolio'
import { LeaderObservationsPanel } from './LeaderObservationsPanel'
import { visibleForSession, visibleForPeriod, buildCarriedMidYearEvidence, isMeasurementEntry, matchesPeriod } from '@/lib/evidenceReleaseFilter'
import ObjectivesKPIsView from '../ObjectivesKPIsView'
import { SelfReflectionPanel } from '../review/SelfReflectionPanel'
import { AIReviewAssistant } from '../review/AIReviewAssistant'
import { KPADiscussionGuide } from '../review/KPADiscussionGuide'
import { VideoHelpButton } from '@/components/guidance/VideoHelpButton'
import { RatingAssistant } from '../review/RatingAssistant'
import { feedbackService, type FeedbackRequest, type HolisticFeedbackAnalysis } from '@/services/api/feedbackService'
import { EnhancedEvidenceEntry, KPAData } from '@/types/evidence'
import { ReviewSynthesis } from '@/services/aiService'

import { performanceService } from '@/services/api/performanceService'
import { getCurrentReviewPeriod } from '@/lib/reviewPeriod'
import { useReviewCycleConfig } from '@/hooks/useReviewCycleConfig'

// Import extracted hooks
import { useReviewSession } from '@/hooks/useReviewSession'
import { useReviewAI } from '@/hooks/useReviewAI'
import { useIDPTransfer } from '@/hooks/useIDPTransfer'

// Import extracted view components
import { ReviewFeedbackView } from '../review/ReviewFeedbackView'
import { ReviewFinalizeView } from '../review/ReviewFinalizeView'
import { NextYearObjectivesStep } from '../review/NextYearObjectivesStep'

interface FeedbackItem {
  id: string
  from: string
  role: string
  date: string
  content: string
  rating?: number
  category: 'stakeholder' | 'upward' | '360'
}

interface ConductReviewWorkspaceProps {
  open: boolean
  onClose: () => void
  memberName: string
  memberId: string
  memberRole?: string // Job title / role of the team member (for the header subtitle)
  memberDepartment?: string // Department of the team member (for the header subtitle)
  managerId?: string // Current user's ID if manager
  reviewType: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin' | 'adhoc'
  scheduledDate?: Date | null // Scheduled date of the review - used to determine period for check-ins
  userRole: 'manager' | 'employee' // Determines if user can rate or only view ratings
  reviewSessionId?: string // Existing review session ID to load
  readOnly?: boolean // For one-up manager calibration view - hides all edit actions
  calibrationContext?: { // Context info shown in calibration read-only mode
    proposedRating?: string
    calibratedRating?: string
    managerName?: string
  }

  // Real KPA data from API
  kpaData: KPAData[]

  // Manager ratings (passed in for employee view, or built by manager)
  managerRatings?: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>
  managerOverallRating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
  managerComments?: Record<string, string> // KPA ID to manager comment
  evidence?: EnhancedEvidenceEntry[]
  feedbackItems?: FeedbackItem[]

  // Evidence handlers
  onAddEvidence?: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined> | void
  onUpdateEvidence?: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => void
  onDeleteEvidence?: (evidenceId: string) => void
  onUploadDocument?: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onUpdateEvidenceClassification?: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => void
  onRefreshEvidence?: () => Promise<void>

  // Feedback handlers
  feedbackRequests?: any[]
  onCreateFeedbackRequest?: (request: any) => void
  onRefreshFeedback?: () => void

  // Review completion callback
  onReviewCompleted?: () => void

  // KPI handlers
  onViewKPIDetails?: (kpi: any) => void
  onUpdateKPIMeasurement?: (kpiId: string, value: number, notes?: string) => void
}

export default function ConductReviewWorkspace({
  open,
  onClose,
  memberName,
  memberId,
  memberRole,
  memberDepartment,
  managerId,
  reviewType,
  scheduledDate,
  userRole,
  reviewSessionId: initialReviewSessionId,
  readOnly = false,
  calibrationContext,
  kpaData,
  evidence = [],
  feedbackItems = [],
  managerRatings = {},
  managerOverallRating,
  managerComments = {},
  onAddEvidence,
  onUpdateEvidence,
  onDeleteEvidence,
  onUploadDocument,
  onUpdateEvidenceClassification,
  onRefreshEvidence,
  onViewKPIDetails,
  onUpdateKPIMeasurement,
  feedbackRequests = [],
  onCreateFeedbackRequest,
  onRefreshFeedback,
  onReviewCompleted
}: ConductReviewWorkspaceProps) {
  // Convert reviewType to review period for filtering
  const reviewCycleCfg = useReviewCycleConfig()
  const reviewPeriod: 'MIDYEAR' | 'YEAREND' =
    reviewType === 'mid-year' || reviewType === 'q1-checkin' ? 'MIDYEAR' :
    reviewType === 'year-end' || reviewType === 'q3-checkin' ? 'YEAREND' :
    getCurrentReviewPeriod(scheduledDate ?? new Date(), reviewCycleCfg)

  // Suppress unused props for future functionality
  void onViewKPIDetails
  void onUpdateKPIMeasurement

  // Shared Simple ↔ Detailed journey view-mode (localStorage-backed).
  // Simple is the calm, judgement-free default: it hides the per-KPA metric
  // chrome (% progress / objective / KPI / evidence / risk chips), the
  // "KPA Ratings" trophy counter, and the Impact & Growth summary card so
  // the editorial reflection stays centre-stage. Detailed restores them.
  const [journeyViewMode] = useJourneyViewMode()
  const isSimpleView = journeyViewMode === 'simple'
  const isTeamMember = userRole === 'employee'

  // Admin escape-hatch: reopen a COMPLETED review back to IN_PROGRESS. Gated
  // to admin / hr_admin (mirrors the backend RequireRole guard). `user.roles`
  // comes from the shared auth provider used across the app.
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const canReopenReview = !!user?.roles?.some(role => role === 'admin' || role === 'hr_admin')
  const [confirmReopenOpen, setConfirmReopenOpen] = useState(false)
  const [isReopening, setIsReopening] = useState(false)

  const [activeKPAIndex, setActiveKPAIndex] = useState(0)
  // Controlled active tab within a KPA, so the sticky footer can step the user
  // through every tab (not just KPA-to-KPA) as one guided flow.
  const [activeTab, setActiveTab] = useState('objectives-kpis')
  const contentScrollRef = useRef<HTMLDivElement>(null)
  // B1 (May 2026 rework): 'self-reflection' is the team member's high-level
  // headlines reflection at the top of the prep flow. Default landing
  // surface for the team member on a formal review (mid-year / year-end)
  // — they should write their reflection before getting into per-KPA detail.
  // Managers default to 'kpa' since they're focused on per-KPA discussion.
  const isFormalReviewLocal = reviewType === 'mid-year' || reviewType === 'year-end'
  const [viewMode, setViewMode] = useState<'self-reflection' | 'kpa' | 'feedback' | 'finalize' | 'impact-portfolio' | 'leader-observations' | 'next-year-objectives'>(
    userRole === 'employee' && isFormalReviewLocal ? 'self-reflection' : 'kpa'
  )
  // The year this check-in reviews, as the check-in stores it: its date can fall in the next
  // year. A year-end check-in is also where next year's objectives are agreed (HR UAT list
  // #27), so it gets a step for them once that year is known; not in the read-only
  // calibration view.
  const [reviewYear, setReviewYear] = useState<number | null>(null)
  const showNextYearStep = reviewType === 'year-end' && !readOnly && reviewYear !== null
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false)
  const [selectedKPI, setSelectedKPI] = useState<{ id: string; name: string; objectiveId: number } | null>(null)

  // Session hook
  const session = useReviewSession({
    memberId,
    managerId,
    memberName,
    reviewType,
    reviewPeriod,
    userRole,
    initialReviewSessionId,
    readOnly,
    kpaData,
    managerRatings,
    managerOverallRating,
    managerComments,
    onClose,
    onReviewCompleted,
  })

  // Feedback state - fetch team member's feedback requests
  const [memberFeedbackRequests, setMemberFeedbackRequests] = useState<FeedbackRequest[]>([])
  const [loadingFeedback, setLoadingFeedback] = useState(false)

  // Holistic feedback analysis - aggregated insights from all stakeholder feedback
  const [holisticFeedback, setHolisticFeedback] = useState<HolisticFeedbackAnalysis | null>(null)
  const [loadingHolisticFeedback, setLoadingHolisticFeedback] = useState(false)

  // AI hook
  const ai = useReviewAI({
    memberId,
    memberName,
    reviewType,
    reviewPeriod,
    isFormalReview: session.isFormalReview,
    evidence,
    holisticFeedback,
    reviewSessionId: session.reviewSessionId,
    isTeamMember,
  })

  // IDP hook
  const idp = useIDPTransfer({
    open,
    memberId,
    managerId,
    memberName,
    overallRating: session.overallRating,
    reviewSessionId: session.reviewSessionId,
    kpaDiscussionPoints: session.kpaDiscussionPoints,
    ratingRationale: ai.ratingRationale,
    reviewSynthesis: ai.reviewSynthesis,
  })

  // Load stored synthesis from session into AI state
  useEffect(() => {
    const loadStoredSynthesis = async () => {
      if (!session.reviewSessionId) return
      try {
        const { reviewService } = await import('@/services/api/reviewService')
        const sessionData = await reviewService.getReviewSession(session.reviewSessionId)
        if (sessionData.review_year) setReviewYear(sessionData.review_year)
        if (sessionData.synthesis_data) {
          ai.setReviewSynthesis(sessionData.synthesis_data as ReviewSynthesis)
          if (import.meta.env.DEV) console.log('📖 Loaded stored synthesis')
        }
      } catch {
        // Ignore - synthesis loading is best-effort
      }
    }
    loadStoredSynthesis()
  }, [session.reviewSessionId])

  // Auto-switch to finalize view for completed reviews
  useEffect(() => {
    if (session.isReviewCompleted) {
      setViewMode('finalize')
    }
  }, [session.isReviewCompleted])

  // No-op handlers for read-only mode
  const noOpEvidenceAdd = useCallback(() => {
    toast.error('Cannot modify entry - check-in is completed')
  }, [])
  const noOpEvidenceUpdate = useCallback(() => {
    toast.error('Cannot modify entry - check-in is completed')
  }, [])
  const noOpEvidenceDelete = useCallback(() => {
    toast.error('Cannot delete entry - check-in is completed')
  }, [])
  const noOpDocumentUpload = useCallback(async () => {
    toast.error('Cannot upload documents - check-in is completed')
  }, [])
  const noOpClassify = useCallback(() => {
    toast.error('Cannot classify entry - check-in is completed')
  }, [])

  // Load team member's feedback requests when workspace opens
  useEffect(() => {
    const loadMemberFeedback = async () => {
      if (!open || !memberId) return

      setLoadingFeedback(true)
      try {
        if (import.meta.env.DEV) console.log('📬 Loading feedback requests for team member:', memberId)
        const requests = await feedbackService.listTeamMemberFeedbackRequests(memberId)
        if (import.meta.env.DEV) console.log('📬 Loaded feedback requests:', requests)
        setMemberFeedbackRequests(requests)
      } catch (error) {
        console.error('❌ Error loading feedback requests:', error)
        setMemberFeedbackRequests([])
      } finally {
        setLoadingFeedback(false)
      }
    }

    loadMemberFeedback()
  }, [open, memberId])

  // Load holistic feedback analysis when workspace opens (for AI enrichment)
  useEffect(() => {
    const loadHolisticFeedback = async () => {
      if (!open || !memberId) return

      setLoadingHolisticFeedback(true)
      try {
        if (import.meta.env.DEV) console.log('🔍 Loading holistic feedback analysis for:', memberId)
        const analysis = await feedbackService.getHolisticFeedbackAnalysis(memberId)
        if (import.meta.env.DEV) console.log('🔍 Holistic feedback analysis:', analysis)
        setHolisticFeedback(analysis)
      } catch (error) {
        if (import.meta.env.DEV) console.log('ℹ️ No holistic feedback available (this is ok):', error)
        setHolisticFeedback(null)
      } finally {
        setLoadingHolisticFeedback(false)
      }
    }

    loadHolisticFeedback()
  }, [open, memberId])

  // Auto-switch to finalize view for calibration read-only mode
  useEffect(() => {
    if (open && readOnly && calibrationContext) {
      setViewMode('finalize')
    }
  }, [open, readOnly, calibrationContext])

  // Refresh feedback requests callback
  const handleRefreshFeedback = useCallback(async () => {
    if (!memberId) return

    setLoadingFeedback(true)
    try {
      const requests = await feedbackService.listTeamMemberFeedbackRequests(memberId)
      setMemberFeedbackRequests(requests)
      toast.success('Feedback refreshed')
    } catch (error) {
      console.error('Error refreshing feedback:', error)
      toast.error('Failed to refresh feedback')
    } finally {
      setLoadingFeedback(false)
    }
  }, [memberId])

  // Suppress unused variables
  void feedbackItems
  void loadingHolisticFeedback

  const { activeKpaData, canRate, isReviewCompleted, isFormalReview, displayRatings, kpaRatings, overallRating, kpaDiscussionPoints, isSaving, ratingJustification } = session
  const currentKPA = activeKpaData[activeKPAIndex]

  // Reopen a completed review (admin / hr_admin only). On success we invalidate
  // the PL Hub's team-summary + team-cycle caches (prefix match → all managers /
  // years) so the reopened session shows as IN_PROGRESS everywhere, and call the
  // parent's onReviewCompleted refresh (re-loads member KPA/session data). The
  // workspace itself is closed so it re-opens against the fresh IN_PROGRESS state.
  const handleReopenReview = useCallback(async () => {
    if (!session.reviewSessionId) return
    setIsReopening(true)
    try {
      await reviewService.reopenReview(session.reviewSessionId)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mgr-team-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['mgr-team-cycle'] }),
      ])
      onReviewCompleted?.()
      toast.success('Review reopened')
      setConfirmReopenOpen(false)
      onClose()
    } catch (error: any) {
      console.error('Error reopening review:', error)
      // Surface the backend reason (e.g. the calibration lock 409) rather than a
      // generic failure, so the acting admin/HR knows why it was blocked.
      toast.error(error?.response?.data?.detail || error?.message || 'Failed to reopen review')
    } finally {
      setIsReopening(false)
    }
  }, [session.reviewSessionId, queryClient, onReviewCompleted, onClose])

  // Top-level Impact Portfolio view — what the team member released FOR THIS
  // check-in session, PLUS every measurement for the session's period (KPI
  // backing data is always shared, including PL-recorded measurements). A Q1
  // check-in therefore shows Q1-released entries + period measurements; an
  // entry released only for Mid/Year-End won't appear in a Q1 workspace.
  // kpaId undefined → mixed view (KPA + General) with chips.
  // Carry-eligible Mid-Year measurements shown as read-only "carried forward"
  // context in the Year-End view (continuing objectives).
  const carriedMidYearEvidence = buildCarriedMidYearEvidence(evidence, kpaData, reviewPeriod)
  // Top-level Impact Portfolio evidence is viewer-aware:
  //  - Team member (own workspace): ALL their entries for this period, so they
  //    can see and release the unreleased ones — matches the per-KPA view.
  //  - People Leader: only what's shared for THIS session (released narratives +
  //    measurements). (The backend already gates the PL's evidence to released.)
  const releasedSessionEvidence = (
    isTeamMember
      ? evidence.filter(e => matchesPeriod(e, reviewPeriod))
      : evidence.filter(e => visibleForSession(e, session.reviewSessionId, reviewPeriod))
  ).concat(carriedMidYearEvidence)
  // With each KPA's numeric id, which saved entries carry, so a KPA tag and the
  // area filter find the entries linked to it (HR UAT list #2).
  const portfolioKpaOptions = kpaData.map(k => ({ id: k.id, numericId: k.numericId, name: k.display_name || k.name }))
  // Cross-KPA KPI list (tagged with kpaId) so the top-level portfolio's Record
  // Measurement flow can narrow the KPI selector once a KPA is picked.
  const portfolioAvailableKPIs = (() => {
    const out: any[] = []
    kpaData.forEach((kpa: any) => {
      const add = (kpi: any) => {
        if (!kpi || out.some(k => k.id === kpi.id)) return
        const periodValue = reviewPeriod === 'MIDYEAR'
          ? (kpi.midyear_value ?? kpi.current_value)
          : (kpi.yearend_value ?? kpi.current_value)
        out.push({ ...kpi, kpaId: kpa.id, current_value: periodValue ?? undefined })
      }
      ;(kpa.underlyingKPIs || []).forEach(add)
      ;(kpa.individualObjectives || []).forEach((obj: any) => (obj.kpis || []).forEach(add))
    })
    return out
  })()

  // Inline share from the prep workspace — the team member can share a
  // (non-measurement) entry right here instead of going to the separate Impact
  // Portfolio tab. Binary: Shared = visible to the People Leader in every
  // check-in for the entry's period. The Shared/Private toggle also doubles as
  // the per-entry indicator. Measurements are auto-shared so the control skips
  // them (see EvidencePortfolio). PL / read-only get none.
  const canReleaseHere = userRole === 'employee' && !readOnly && !isReviewCompleted
  const handleReleaseEvidence = useCallback(async (evidenceId: string) => {
    const { evidence: updated } = await performanceService.releaseEvidence(memberId, evidenceId)
    await onRefreshEvidence?.()
    return updated as any
  }, [memberId, onRefreshEvidence])
  const handleWithdrawEvidence = useCallback(async (evidenceId: string) => {
    const { evidence: updated } = await performanceService.withdrawEvidence(memberId, evidenceId)
    await onRefreshEvidence?.()
    return updated as any
  }, [memberId, onRefreshEvidence])

  // Get or initialize discussion points for current KPA
  const getCurrentKPAPoints = (step: 'wentWell' | 'challenges' | 'actions') => {
    if (!currentKPA) return []

    // Both sides' points, whichever key each was loaded under (HR UAT list #4)
    const possibleKeys = discussionPointKeys(currentKPA)

    if (import.meta.env.DEV) {
      console.log(`🔍 [${step}] Looking for discussion points - KPA Index: ${activeKPAIndex}, KPA: ${currentKPA.display_name}`)
      console.log(`   Possible keys:`, possibleKeys)
      console.log(`   Available keys:`, Object.keys(kpaDiscussionPoints))
    }

    const seen = new Set<string>()
    const merged: DiscussionPoint[] = []
    for (const key of possibleKeys) {
      if (key && kpaDiscussionPoints[key]) {
        const points = kpaDiscussionPoints[key][step] || []
        for (const p of points) {
          if (!seen.has(p.id)) {
            seen.add(p.id)
            merged.push(p)
          }
        }
      }
    }

    if (import.meta.env.DEV) {
      if (merged.length > 0) {
        console.log(`   ✅ Found ${merged.length} ${step} points`)
      } else {
        console.log(`   ❌ No discussion points found for any key`)
      }
    }

    return merged
  }

  const getRatingColor = (rating: string | null) => {
    // Color/cohesion decision: keep the traffic-light RAG only in Detailed
    // view. In Simple (default) the rating badges use the calm stone palette,
    // with emerald reserved for genuine achievement ('exceptional' / 'full').
    if (isSimpleView) {
      switch (rating) {
        case 'exceptional': return 'bg-emerald-50 text-emerald-800 border-emerald-200'
        case 'full': return 'bg-emerald-50 text-emerald-700 border-emerald-200'
        case 'not-yet-full': return 'bg-stone-100 text-stone-700 border-stone-300'
        case 'weak': return 'bg-stone-100 text-stone-700 border-stone-300'
        case 'tstr': return 'bg-slate-100 text-slate-600 border-slate-300'
        default: return 'bg-stone-100 text-stone-600 border-stone-200'
      }
    }
    switch (rating) {
      case 'exceptional': return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'full': return 'bg-green-100 text-green-800 border-green-200'
      case 'not-yet-full': return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'weak': return 'bg-red-100 text-red-800 border-red-200'
      case 'tstr': return 'bg-slate-100 text-slate-700 border-slate-300'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getRatingLabel = (rating: string | null) => {
    if (!rating) return 'Not Rated'
    if (rating === 'tstr') return 'Too Soon to Rate'
    return rating.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  // Filter evidence for current KPA (carried Mid-Year measurements included).
  const currentKPAEvidence = currentKPA ? [...evidence, ...carriedMidYearEvidence].filter(e => {
    // Carried entries are Mid-Year but intentionally shown in the Year-End view.
    if (e.reviewPeriod && e.reviewPeriod !== reviewPeriod && !e.carriedFromMidYear) return false

    // Evidence kpaId arrives from the API as the NUMERIC KPA PK (stringified),
    // while currentKPA.id is the name slug in the conduct-review data shape.
    // Match against both forms (String-coerced) so manager observations and
    // employee evidence linked to this KPA actually surface here.
    const numericId = (currentKPA as any).numericId
    const matchesKPA = e.kpaId === currentKPA.id ||
      (numericId != null && e.kpaId != null && String(e.kpaId) === String(numericId)) ||
      currentKPA.individualObjectives?.some((obj: any) =>
        obj.id.toString() === e.objectiveId ||
        obj.kpis?.some((kpi: any) => kpi.id === e.kpiId)
      ) ||
      currentKPA.underlyingKPIs?.some((kpi: any) => kpi.id === e.kpiId)

    if (matchesKPA) return true

    // General (unlinked) entries no longer leak into every KPA panel — they
    // now live in the dedicated top-level Impact Portfolio tab. Keep them out
    // of the per-KPA view so a KPA shows only its own entries.
    return false
  }) : []

  // Extract available KPIs from current KPA. Source from BOTH the objectives'
  // nested kpis AND the KPA's underlyingKPIs, de-duped by id — in the
  // conduct-review data shape the objectives often carry no nested `kpis`, so
  // relying on them alone left the "Link to KPI" picker empty and the new
  // Record Measurement flow uncompletable. Mirrors the employee-side
  // EvidencePortfolio/MemberKPAPanel availableKPIs builder.
  const availableKPIs = currentKPA ? (() => {
    const fromObjectives = (currentKPA.individualObjectives || []).flatMap((obj: any) =>
      (obj.kpis || []).map((kpi: any) => ({
        id: kpi.id,
        name: kpi.name,
        description: kpi.description,
        unit: kpi.unit,
        target_value: kpi.target_value,
        current_value: kpi.current_value,
        // EvidencePortfolio scopes the "Link to KPI" picker to the active KPA
        // (scopedKPIs filters on k.kpaId === effectiveKpaId). Without this the
        // picker is silently empty even though KPIs exist for the KPA.
        kpaId: currentKPA.id,
        objectiveTitle: obj.title,
        objectiveId: obj.id,
        measurement_history: kpi.measurement_history,
        measurement_mode: kpi.measurement_mode,
        measurement_direction: kpi.measurement_direction
      }))
    )
    const seen = new Set(fromObjectives.map((k: any) => k.id))
    const fromUnderlying = (currentKPA.underlyingKPIs || [])
      .filter((kpi: any) => !seen.has(kpi.id))
      .map((kpi: any) => ({
        id: kpi.id,
        name: kpi.name,
        description: (kpi as any).description,
        unit: kpi.unit,
        target_value: kpi.target_value,
        current_value: kpi.current_value,
        kpaId: currentKPA.id,
        objectiveTitle: undefined,
        objectiveId: undefined,
        measurement_history: (kpi as any).measurement_history,
        measurement_mode: (kpi as any).measurement_mode,
        measurement_direction: (kpi as any).measurement_direction
      }))
    return [...fromObjectives, ...fromUnderlying]
  })() : []

  // Leader Observations tab — only a People Leader viewing a member (not
  // self-prep) gets the PL-private observations surface. The team member never
  // sees it. The evidence handlers must be present for the EvidencePortfolio to
  // render in observationsMode (otherwise the tab would be empty/non-functional).
  const showLeaderObservationsTab =
    userRole === 'manager' && !!managerId && managerId !== memberId &&
    !!onAddEvidence && !!onDeleteEvidence && !!onUploadDocument
  // Count of this PL's own observations on the current KPA (source='manager',
  // non-measurement). The server already scopes observations to this PL, so a
  // client-side source filter is sufficient for the tab counter.
  const currentKPALeaderObservations = currentKPAEvidence.filter(
    e => e.source === 'manager' && e.type !== 'measurement'
  )

  // Helper to get rating for a KPA
  const getKPARating = (kpa: any): 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null => {
    if (!kpa) return null
    const possibleKeys = [kpa.id, kpa.name, kpa.display_name].filter(Boolean) as string[]
    for (const key of possibleKeys) {
      if (key && displayRatings[key]) {
        return displayRatings[key]
      }
    }
    return null
  }

  const allKPAsRated = activeKpaData.every(kpa => getKPARating(kpa) !== null)
  const ratedKPAsCount = Object.values(displayRatings).filter(r => r !== null).length
  const currentKPARating = getKPARating(currentKPA)

  const getReviewTypeLabel = () => {
    const labels = {
      'year-end': 'Year-End 1:1',
      'mid-year': 'Mid-Year 1:1',
      'q1-checkin': 'Q1 Check-in',
      'q3-checkin': 'Q3 Check-in',
      'adhoc': 'On-Demand Check-in'
    }
    return labels[reviewType] || 'Check-in'
  }

  // Handle KPI evidence actions
  const handleAddKPIEvidence = useCallback((kpiId: string, kpiName: string, objectiveId: number) => {
    setSelectedKPI({ id: kpiId, name: kpiName, objectiveId })
    setEvidenceModalOpen(true)
  }, [])

  const handleAddKPISpecificEvidence = useCallback(async (evidenceData: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>): Promise<EnhancedEvidenceEntry | undefined> => {
    if (selectedKPI && onAddEvidence) {
      const result = await onAddEvidence({
        ...evidenceData,
        kpiId: selectedKPI.id,
        objectiveId: evidenceData.objectiveId || selectedKPI.objectiveId.toString(),
        kpaId: evidenceData.kpaId || currentKPA.id
      })
      setEvidenceModalOpen(false)
      toast.success('Entry added successfully')
      return result as EnhancedEvidenceEntry | undefined
    }
    return undefined
  }, [selectedKPI, currentKPA, onAddEvidence])

  // Top-level workspace steps (the masthead tabs), in flow order. The sticky
  // footer's Back/Next walk this sequence so the whole check-in reads as one
  // guided flow across every tab — not just KPA-to-KPA. Mirrors the tab strip's
  // visibility: headlines only on formal reviews; Finalise only when ratable.
  type WorkspaceStep = {
    mode: 'self-reflection' | 'kpa' | 'impact-portfolio' | 'leader-observations' | 'feedback' | 'next-year-objectives' | 'finalize'
    kpaIndex?: number
    label: string
  }
  const workspaceSteps: WorkspaceStep[] = [
    ...(isFormalReview
      ? [{ mode: 'self-reflection' as const, label: userRole === 'employee' ? 'My headlines' : `${memberName.split(' ')[0]}'s headlines` }]
      : []),
    ...activeKpaData.map((kpa, i) => ({ mode: 'kpa' as const, kpaIndex: i, label: kpa.display_name || kpa.name })),
    { mode: 'impact-portfolio' as const, label: 'Impact Portfolio' },
    ...(showLeaderObservationsTab ? [{ mode: 'leader-observations' as const, label: 'Leader Observations' }] : []),
    { mode: 'feedback' as const, label: 'Feedback' },
    ...(showNextYearStep ? [{ mode: 'next-year-objectives' as const, label: `${reviewYear! + 1} objectives` }] : []),
    ...((canRate || isReviewCompleted)
      ? [{ mode: 'finalize' as const, label: isReviewCompleted ? (isFormalReview ? 'Final Impact & Growth Indicator' : 'Summary') : 'Finalise' }]
      : []),
  ]
  const currentStepIndex = workspaceSteps.findIndex(
    s => s.mode === viewMode && (s.mode !== 'kpa' || s.kpaIndex === activeKPAIndex),
  )
  const isFirstStep = currentStepIndex <= 0
  const isLastStep = currentStepIndex === workspaceSteps.length - 1

  const goToStep = (step: WorkspaceStep) => {
    setViewMode(step.mode)
    if (step.mode === 'kpa' && step.kpaIndex !== undefined) {
      setActiveKPAIndex(step.kpaIndex)
      setActiveTab('objectives-kpis') // land on the KPA's first sub-tab
    }
  }
  // Jump straight to a KPA from the top-strip pills.
  const goToKPA = (index: number) => goToStep({ mode: 'kpa', kpaIndex: index, label: '' })
  const goToPrevStep = () => { if (currentStepIndex > 0) goToStep(workspaceSteps[currentStepIndex - 1]) }
  const goToNextStep = () => {
    if (currentStepIndex >= 0 && currentStepIndex < workspaceSteps.length - 1) goToStep(workspaceSteps[currentStepIndex + 1])
  }
  const nextStepLabel = currentStepIndex >= 0 && currentStepIndex < workspaceSteps.length - 1
    ? `Next: ${workspaceSteps[currentStepIndex + 1].label}`
    : 'Done'

  // Reset scroll to top whenever the workspace OPENS or the step changes
  // (view / KPA / sub-tab). `open` is in the deps so a one-up opening a
  // read-only review always lands at the top rather than at a stale scroll
  // position. `contentScrollRef` is attached to whichever view container is
  // currently mounted (KPA / feedback / finalize / impact), so this resets
  // the right scroller regardless of which view is active. requestAnimationFrame
  // waits for the new view to mount before scrolling.
  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => contentScrollRef.current?.scrollTo({ top: 0 }))
  }, [open, activeTab, activeKPAIndex, viewMode])

  if (!open) return null

  // Show message when employee has no objectives/KPAs to review
  if (!currentKPA && activeKpaData.length === 0) {
    return createPortal(
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">No objectives for this check-in</h3>
            <p className="text-gray-600">
              {isTeamMember
                ? 'You do not have any approved objectives set for this period.'
                : `${memberName} does not have any approved objectives set for this period.`}
              {' '}Objectives need to be configured before the check-in.
            </p>
            <Button onClick={onClose} className="mt-2">
              Close
            </Button>
          </div>
        </div>
      </motion.div>,
      document.body
    )
  }

  // Show loading state while KPA data is being fetched
  if (!currentKPA) {
    return createPortal(
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-heritage-blue"></div>
            <p className="text-gray-600">Loading check-in data...</p>
          </div>
        </div>
      </motion.div>,
      document.body
    )
  }

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-[1536px] h-[95vh] flex flex-col overflow-hidden">
        {/* Masthead — single compact navy band. Replaces the former two
            stacked gradient heroes (identity + AI/stepper) that together
            consumed ~45% of the dialog height. Identity sits left; the
            review-type pill, status badges, the AI "Synthesise themes"
            trigger and the close button sit right. The KPA stepper and
            current-KPA context now live on white directly below. */}
        <div className="bg-gradient-to-br from-heritage-blue to-future-blue text-white px-6 py-3 flex-shrink-0 border-b border-white/15 relative" data-tour="review-header">
          <div className="absolute inset-0 bg-heritage-blue/20 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}>
            {/* Identity: avatar + name + role • dept on a single row */}
            <div className="flex items-center gap-3 min-w-0">
              {userRole !== 'employee' && (
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-white">
                    {memberName.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <h2 className="text-lg font-serif font-bold text-white leading-tight truncate">
                  {userRole === 'employee' ? 'My reflection' : memberName}
                </h2>
                {userRole !== 'employee' && (memberRole || memberDepartment) && (
                  <p className="text-blue-100 text-xs leading-tight truncate">
                    {memberRole}{memberRole && memberDepartment ? ' • ' : ''}{memberDepartment}
                  </p>
                )}
              </div>
            </div>
            {/* Right cluster: review-type + status + AI trigger + close */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge className="text-xs px-2.5 py-1 bg-white/20 text-white border border-white/30 font-semibold whitespace-nowrap">
                {getReviewTypeLabel()}
              </Badge>
              {isReviewCompleted && (
                <Badge className="text-xs px-2.5 py-1 bg-green-500/30 text-green-100 border border-green-400/40 font-semibold flex items-center gap-1 whitespace-nowrap">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Completed
                </Badge>
              )}
              {/* Admin escape-hatch: reopen a completed review (admin / hr_admin
                  only). Unobtrusive outline treatment on the navy masthead; not
                  offered in the read-only calibration view. */}
              {isReviewCompleted && canReopenReview && !readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmReopenOpen(true)}
                  className="h-8 border-white/30 bg-white/15 text-white hover:bg-white/25 whitespace-nowrap"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Reopen review
                </Button>
              )}
              {readOnly && !isReviewCompleted && (
                <Badge className="text-xs px-2.5 py-1 bg-amber-500/30 text-amber-100 border border-amber-400/40 font-semibold flex items-center gap-1 whitespace-nowrap">
                  <Lock className="h-3.5 w-3.5" />
                  Read only
                </Badge>
              )}
              {canRate && isSaving && (
                <Badge className="text-xs px-2.5 py-1 bg-blue-400/30 text-blue-100 border border-blue-300/40 font-semibold">
                  Saving…
                </Badge>
              )}
              {/* View-mode toggle — lets the People Leader flip Simple/Detailed.
                  Reads/writes the global journeyViewMode hook that already drives
                  the !isSimpleView branches below. Wrapper lightens the toggle's
                  muted label so it reads on the navy masthead. */}
              <VideoHelpButton
                surface="checkin-dialogue"
                iconOnly
                className="border-white/30 bg-white/15 text-white hover:bg-white/25"
              />
              <ViewModeToggle className="[&_span]:text-blue-100" />
              {/* AI readiness check — a quiet icon-button trigger. The result
                  panel renders on white below the stepper once generated.
                  Available to BOTH the team member (self-prep) and the People
                  Leader; hidden once insights exist (the panel owns regen). */}
              {!ai.aiInsights && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => ai.loadAIInsights(activeKpaData, currentKPA)}
                  disabled={ai.isLoadingAIInsights}
                  title={ai.isLoadingAIInsights
                    ? 'Checking readiness…'
                    : isTeamMember
                      ? 'Check your readiness for this check-in'
                      : `Check ${memberName}'s readiness for this check-in`}
                  className="text-white hover:bg-white/15 h-9 w-9"
                >
                  {ai.isLoadingAIInsights ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-white hover:bg-white/10 h-9 w-9 flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Calibration Context Banner */}
        {readOnly && calibrationContext && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-amber-800 font-medium">People Leader:</span>
                  <Badge variant="outline" className="text-amber-700 border-amber-300">
                    {calibrationContext.managerName || 'Unknown'}
                  </Badge>
                </div>
                <div className="h-4 w-px bg-amber-300" />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-amber-800 font-medium">Proposed Impact & Growth Indicator:</span>
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                    {calibrationContext.proposedRating?.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Not Set'}
                  </Badge>
                </div>
                {calibrationContext.calibratedRating && calibrationContext.calibratedRating !== calibrationContext.proposedRating && (
                  <>
                    <div className="h-4 w-px bg-amber-300" />
                    <div className="flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-amber-600" />
                      <span className="text-sm text-amber-800 font-medium">Calibrated:</span>
                      <Badge className="bg-green-100 text-green-800 border border-green-300">
                        {calibrationContext.calibratedRating.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge>
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-amber-600">
                Viewing check-in context for calibration purposes
              </p>
            </div>
          </div>
        )}

        {/* KPA stepper + current-KPA context — slim, on white. Was a second
            navy gradient band stacking the AI banner, a chunky min-h-14 tab
            strip and a white/10 "KPA Details" row. Now one restrained accent
            (navy lives only in the masthead) and roughly half the height. */}
        <div className="bg-white border-b border-gray-200 px-6 pt-3 pb-3 flex-shrink-0">
          {/* Current-KPA eyebrow + name + (Detailed) metric chips. Only in
              KPA view — the self-reflection / feedback / finalise steps have
              no per-KPA context to show. Replaces the white/10 details band. */}
          {viewMode === 'kpa' && (
            <div className="flex items-center justify-between gap-3 mb-2.5">
              <div className="min-w-0">
                <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-gray-400 leading-none mb-1">
                  KPA {activeKPAIndex + 1} of {activeKpaData.length}
                </p>
                <h3 className="font-serif text-lg font-semibold text-gray-900 leading-tight truncate">
                  {currentKPA.display_name}
                </h3>
              </div>
              {/* #18 / #37: per-KPA metric chips are dashboard chrome that
                  undermines the calm reflection in Simple view — Detailed only. */}
              {!isSimpleView && (
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="flex items-center gap-1.5 text-sm">
                    <TrendingUp className="h-3.5 w-3.5 text-blue-500" />
                    <span className="font-semibold text-gray-700">{currentKPA.averageProgress || 0}%</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-sm">
                    <Target className="h-3.5 w-3.5 text-green-600" />
                    <span className="font-semibold text-gray-700">{currentKPA.individualObjectives?.length || 0}</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-sm">
                    <BarChart4 className="h-3.5 w-3.5 text-purple-600" />
                    <span className="font-semibold text-gray-700">{currentKPA.underlyingKPIs?.length || 0}</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-sm">
                    <FileText className="h-3.5 w-3.5 text-amber-600" />
                    <span className="font-semibold text-gray-700">{currentKPAEvidence.length}</span>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Stepper row — slim, natural-width pills that wrap when narrow.
              Plain buttons (viewMode is custom state, not Radix Tabs). */}
          <div className="flex flex-wrap items-center gap-1.5 w-full min-w-0 max-w-full" data-tour="kpa-navigator">
            {/* B1: Self-Reflection tab — only on formal reviews (mid-year /
                year-end). Anchors at the start of the tab strip so the team
                member's high-level headlines come first in the prep flow.
                Managers see it too (read-only on their side via canEdit),
                but they can navigate straight to KPAs. */}
            {isFormalReview && (
              <button
                className={`gap-1.5 h-9 px-3 flex-shrink-0 flex items-center justify-center rounded-md transition-colors ${
                  viewMode === 'self-reflection'
                    ? 'bg-heritage-blue text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setViewMode('self-reflection')}
              >
                <Sparkles className="h-4 w-4 flex-shrink-0" />
                {/* Self-reflection is the team member's OWN headlines. "My"
                    only makes sense when the member is viewing it; the People
                    Leader sees the member's first-name possessive instead. */}
                <span className="text-sm font-medium whitespace-nowrap">
                  {userRole === 'employee'
                    ? 'My headlines'
                    : `${memberName.split(' ')[0]}'s headlines`}
                </span>
              </button>
            )}
            {activeKpaData.map((kpa, index) => {
              const isRated = kpaRatings[kpa.id] !== undefined && kpaRatings[kpa.id] !== null
              const isActive = viewMode === 'kpa' && index === activeKPAIndex
              const isCompleted = isFormalReview ? isRated : false

              return (
                <button
                  key={kpa.id}
                  className={`gap-1.5 h-9 px-3 flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${
                    isActive
                      ? 'bg-heritage-blue text-white border-heritage-blue'
                      : 'bg-blue-50/60 border-blue-100 text-gray-700 hover:bg-blue-100/60'
                  }`}
                  onClick={() => goToKPA(index)}
                  title={kpa.display_name || kpa.name}
                >
                  <BarChart4 className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium truncate max-w-[200px]">
                    {kpa.display_name || kpa.name}
                  </span>
                  {/* Per-KPA completion metric — Detailed only. The nav chrome
                      (icon + filled pill) is identical in both modes. */}
                  {!isSimpleView && isCompleted && (
                    <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 opacity-80" />
                  )}
                </button>
              )
            })}

            {/* Impact Portfolio Tab — the team member's released portfolio for
                THIS check-in (KPA + General), respecting their release toggles. */}
            <button
              className={`gap-1.5 h-9 px-3 flex-shrink-0 flex items-center justify-center rounded-md transition-colors ${
                viewMode === 'impact-portfolio'
                  ? 'bg-heritage-blue text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setViewMode('impact-portfolio')}
              data-tour="review-portfolio-tab"
            >
              <FolderOpen className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium whitespace-nowrap">Impact Portfolio</span>
            </button>

            {/* Leader Observations Tab — every observation the People Leader recorded
                for the period, tagged by KPA or General, as on the member card (HR UAT
                list #2). Only for a People Leader viewing a member's check-in. */}
            {showLeaderObservationsTab && (
              <button
                className={`gap-1.5 h-9 px-3 flex-shrink-0 flex items-center justify-center rounded-md transition-colors ${
                  viewMode === 'leader-observations'
                    ? 'bg-heritage-blue text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setViewMode('leader-observations')}
              >
                <NotebookPen className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">Leader Observations</span>
              </button>
            )}

            {/* Feedback Tab */}
            <button
              className={`gap-1.5 h-9 px-3 flex-shrink-0 flex items-center justify-center rounded-md transition-colors ${
                viewMode === 'feedback'
                  ? 'bg-heritage-blue text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setViewMode('feedback')}
              data-tour="review-feedback-tab"
            >
              <MessageSquare className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium whitespace-nowrap">Feedback</span>
            </button>

            {/* Next year's objectives, agreed in this check-in (HR UAT list #27) */}
            {showNextYearStep && (
              <button
                className={`gap-1.5 h-9 px-3 flex-shrink-0 flex items-center justify-center rounded-md transition-colors ${
                  viewMode === 'next-year-objectives'
                    ? 'bg-heritage-blue text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setViewMode('next-year-objectives')}
              >
                <CalendarPlus className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{reviewYear! + 1} objectives</span>
              </button>
            )}

            {/* Finalise Tab */}
            {(canRate || isReviewCompleted) && (
              <button
                className={`gap-1.5 h-9 px-3 flex-shrink-0 flex items-center justify-center rounded-md transition-colors ${
                  viewMode === 'finalize'
                    ? 'bg-heritage-blue text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setViewMode('finalize')}
                data-tour="review-finalize-tab"
              >
                <Lock className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">
                  {isReviewCompleted ? (isFormalReview ? 'Final Impact & Growth Indicator' : 'Summary') : 'Finalise'}
                </span>
                {/* Per-tab completion metric — Detailed only. */}
                {!isSimpleView && overallRating && (
                  <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 opacity-80" />
                )}
              </button>
            )}
          </div>

          {/* AI readiness / synthesised-themes panel — renders here on white
              once triggered from the masthead Sparkles button. Available to BOTH
              the team member (self-prep) and the People Leader, and in EVERY
              step (not just the KPA view) so the result shows wherever it was
              triggered. */}
          {ai.aiInsights && (
            <div className="mt-3">
              <AIReviewAssistant
                insights={ai.aiInsights}
                isLoading={ai.isLoadingAIInsights}
                onGenerateInsights={() => ai.loadAIInsights(activeKpaData, currentKPA)}
                employeeName={memberName}
              />
            </div>
          )}
        </div>

        {/* Main Content Area - Self-Reflection View (B1, May 2026 rework).
            Anchored at the start of the prep flow on formal reviews. Only
            the team member can edit; the People Leader sees it read-only.
            Edits lock once the session moves past pending / scheduled
            (the backend enforces a 409 in that case). */}
        {viewMode === 'self-reflection' && session.reviewSessionId && (
          <div ref={contentScrollRef} className="flex-1 overflow-y-auto px-6 py-6">
            <RecordedByHrNote recordedByHr={session.recordedByHr} calibrationRecordedByHr={session.calibrationRecordedByHr} />
            <SelfReflectionPanel
              sessionId={session.reviewSessionId}
              canEdit={userRole === 'employee' && !isReviewCompleted && !readOnly}
              memberName={memberName}
            />
          </div>
        )}

        {/* Main Content Area - KPA View */}
        {viewMode === 'kpa' && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          {/* Released-entries summary — A2 (May 2026 rework) replaced the
              whole-portfolio curate panel with per-entry release on the
              Impact Portfolio itself. This banner just reports how many
              entries the team member has already released for THIS check-in
              and points them back at the portfolio if they want to release
              more or withdraw. */}
          {userRole === 'employee' && !readOnly && session.reviewSessionId && (() => {
            // "Shared" = entries the PL will see for this check-in: binary-shared
            // narratives PLUS measurements (always shared automatically). Binary +
            // period-aware — an entry shared for its period shows in every check-in
            // of that period (NOT keyed to this session).
            const sharedForThisSession = evidence.filter(e => visibleForPeriod(e, reviewPeriod))
            const count = sharedForThisSession.length
            const label =
              reviewType === 'mid-year' ? 'Mid-Year' :
              reviewType === 'year-end' ? 'Year-End' :
              reviewType === 'q1-checkin' ? 'Q1 Check-in' :
              reviewType === 'q3-checkin' ? 'Q3 Check-in' :
              'check-in'
            const managerNamePart = calibrationContext?.managerName
              ? ` with ${calibrationContext.managerName}`
              : ''
            return (
              <div className="px-6 pt-6 pb-2">
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  {count === 0 ? (
                    <>
                      <span className="font-medium">Nothing shared yet.</span>{' '}
                      Open your <span className="font-medium">Impact Portfolio</span> and
                      share the entries you want to discuss at your {label} check-in
                      {managerNamePart}. (KPI measurements are shared automatically.)
                    </>
                  ) : (
                    <>
                      <span className="font-medium">
                        {count} {count === 1 ? 'entry' : 'entries'} shared
                      </span>{' '}
                      for this {label} check-in{managerNamePart} — measurements are shared
                      automatically; share any other entries you want to discuss. Manage
                      sharing in your Impact Portfolio (entries stay editable until the check-in begins).
                    </>
                  )}
                </div>
              </div>
            )
          })()}
          {/* #20: the inner tab counter distinguishes total entries from what
              the People Leader will actually SEE for this check-in. "Shared" =
              entries the team member released for this session PLUS measurements
              (KPI backing data is always shared automatically, never released
              via the toggle) — so a KPA of measurements no longer reads
              "N entries · 0 released", which looked broken. */}
          {(() => {
            const sharedForThisKPA = currentKPAEvidence.filter(
              e => visibleForPeriod(e, reviewPeriod)
            ).length
            return (
              <div className="sticky top-0 z-10 bg-white px-6 pt-6 pb-2 shadow-sm border-b border-gray-200">
                <TabsList className={`grid w-full grid-cols-1 ${showLeaderObservationsTab ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} h-auto`}>
                  <TabsTrigger value="objectives-kpis" data-tour="kpa-objectives-tab" className="whitespace-normal">Objectives & KPIs ({currentKPA.individualObjectives?.length || 0}/{currentKPA.underlyingKPIs?.length || 0})</TabsTrigger>
                  <TabsTrigger value="evidence" data-tour="kpa-evidence-tab" className="whitespace-normal">
                    Impact Portfolio · {currentKPAEvidence.length} {currentKPAEvidence.length === 1 ? 'entry' : 'entries'}
                    {session.reviewSessionId ? ` · ${sharedForThisKPA} shared` : ''}
                  </TabsTrigger>
                  {/* Leader Observations — PL-private notes (source='manager').
                      Only shown to a People Leader viewing a member (not self-prep);
                      the team member never sees this tab. Surfaces the same
                      observationsMode EvidencePortfolio that used to be embedded
                      at the bottom of the 1:1 dialogue tab. */}
                  {showLeaderObservationsTab && (
                    <TabsTrigger value="leader-observations" data-tour="kpa-leader-observations-tab" className="whitespace-normal">
                      Leader Observations · {currentKPALeaderObservations.length} {currentKPALeaderObservations.length === 1 ? 'note' : 'notes'}
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="discussion" className="whitespace-normal">1:1 check-in dialogue</TabsTrigger>
                </TabsList>
              </div>
            )
          })()}

          <div ref={contentScrollRef} className="flex-1 overflow-y-auto px-6 py-6">
            <RecordedByHrNote recordedByHr={session.recordedByHr} calibrationRecordedByHr={session.calibrationRecordedByHr} />
            {/* Combined Objectives & KPIs Tab */}
            <TabsContent value="objectives-kpis" className="m-0 space-y-6">
              <ObjectivesKPIsView
                objectives={currentKPA.individualObjectives}
                evidence={currentKPAEvidence}
                userRole={userRole}
                onAddKPIEvidence={handleAddKPIEvidence}
                reviewPeriod={reviewPeriod}
                journeyViewMode={journeyViewMode}
              />
            </TabsContent>

            {/* Leader Observations Tab — PL-private notes (source='manager').
                Mirrors the Impact Portfolio EvidencePortfolio usage but in
                observationsMode: only the People Leader can add, the team member
                never sees these, and KPI/measurement affordances are suppressed.
                Replaces the observations card that used to live at the bottom of
                the 1:1 dialogue tab (HR feedback: "not sure where the buttons
                are"). */}
            {/* Leader Observations renders a bare EvidencePortfolio at the
                shared wrapper's px-6 inset, mirroring the Impact Portfolio tab.
                The previous outer Card + "My observations for {KPA}" header was
                removed: it duplicated EvidencePortfolio's own observations-mode
                header ("LEADER OBSERVATIONS · PRIVATE TO YOU") and pushed the
                panel to a deeper inset than Impact, making the two sub-tabs look
                inconsistent. The selected KPA pill already supplies the KPA
                context. */}
            {showLeaderObservationsTab && (
              <TabsContent value="leader-observations" className="m-0" data-tour="kpa-pl-observations">
                    <EvidencePortfolio
                      evidence={currentKPAEvidence}
                      kpaId={currentKPA.id}
                      // This KPA, so each observation shows its KPA tag (HR UAT list #2).
                      kpaOptions={[{ id: currentKPA.id, numericId: currentKPA.numericId,
                                     name: currentKPA.display_name || currentKPA.name }]}
                      availableKPIs={availableKPIs}
                      userRole={userRole}
                      viewMode={journeyViewMode}
                      currentUserId={managerId}
                      targetUserId={memberId}
                      reviewPeriod={reviewPeriod}
                      observationsMode={true}
                      hideUploadButton={true}
                      readOnly={isReviewCompleted}
                      ownerDisplayName={memberName}
                      onAddEvidence={isReviewCompleted ? noOpEvidenceAdd as any : onAddEvidence}
                      onUpdateEvidence={isReviewCompleted ? noOpEvidenceUpdate as any : onUpdateEvidence}
                      onDeleteEvidence={isReviewCompleted ? noOpEvidenceDelete as any : onDeleteEvidence}
                      onUploadDocument={isReviewCompleted ? noOpDocumentUpload as any : onUploadDocument}
                      onClassifyEvidence={isReviewCompleted ? noOpClassify as any : onUpdateEvidenceClassification}
                      onRefreshEvidence={onRefreshEvidence}
                    />
              </TabsContent>
            )}

            {/* 1:1 check-in dialogue Tab */}
            <TabsContent value="discussion" className="m-0 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-heritage-blue" />
                    {currentKPA.name}
                  </CardTitle>
                  <p className="text-sm text-gray-600">{currentKPA.description}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Three focused discussion areas with character limits for efficiency
                  </p>
                </CardHeader>
              </Card>

              {/* AI Discussion Guidance */}
              <KPADiscussionGuide
                guidance={ai.kpaGuidance[currentKPA.id] || null}
                isLoading={ai.loadingGuidanceFor === currentKPA.id}
                onGenerateGuidance={() => ai.loadKPAGuidance(currentKPA, currentKPAEvidence, kpaDiscussionPoints)}
                isVisible={true}
              />

              {/* Unified 3-Step Process for ALL Review Types */}
              <div className="space-y-6" data-tour="kpa-discussion">
                  {/* Step 1: What went well (was achieved).
                      A5 (May 2026 rework): wording aligned to the HR/people-
                      leader feedback — "stick with what went well (was
                      achieved) and what were the challenges (not achieved
                      and why)" — so each panel speaks the same language as
                      the source feedback document. The free-text framing
                      is preserved (NOT a binary achieved/not-achieved
                      indicator on objectives, which was explicitly rejected
                      in the feedback). */}
                  <Card className="border-l-4 border-l-green-500">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        1. What went well (was achieved)
                      </CardTitle>
                      <p className="text-sm text-gray-600">Capture wins, milestones hit, and outcomes you're proud of in this KPA.</p>
                    </CardHeader>
                    <CardContent>
                      <DiscussionItems
                        points={getCurrentKPAPoints('wentWell')}
                        onAddPoint={(content) => session.handleAddPoint(currentKPA.id, 'wentWell', content)}
                        onDeletePoint={(pointId) => session.handleDeletePoint(currentKPA.id, 'wentWell', pointId)}
                        onUpdatePoint={(pointId, content) => session.handleUpdatePoint(currentKPA.id, 'wentWell', pointId, content)}
                        currentUserRole={userRole}
                        canEdit={!isReviewCompleted}
                        placeholder="E.g., Successfully delivered project 2 weeks ahead of schedule..."
                        maxLength={500}
                      />
                    </CardContent>
                  </Card>

                  {/* Step 2: Challenges (not achieved and why).
                      #19: challenges aren't warnings — the orange ⚠ framed them
                      as risk. Swapped to a neutral leaf marker (matching the
                      calm successes ✓) so the panel reads as reflection, not
                      alarm. */}
                  <Card className="border-l-4 border-l-stone-400">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Leaf className="h-5 w-5 text-stone-500" />
                        2. Challenges (not achieved and why)
                      </CardTitle>
                      <p className="text-sm text-gray-600">What didn't land, what got in the way, and the root causes behind it.</p>
                    </CardHeader>
                    <CardContent>
                      <DiscussionItems
                        points={getCurrentKPAPoints('challenges')}
                        onAddPoint={(content) => session.handleAddPoint(currentKPA.id, 'challenges', content)}
                        onDeletePoint={(pointId) => session.handleDeletePoint(currentKPA.id, 'challenges', pointId)}
                        onUpdatePoint={(pointId, content) => session.handleUpdatePoint(currentKPA.id, 'challenges', pointId, content)}
                        currentUserRole={userRole}
                        canEdit={!isReviewCompleted}
                        placeholder="E.g., Limited resources impacted our ability to meet all deadlines..."
                        maxLength={500}
                      />
                    </CardContent>
                  </Card>

                  {/* Step 3: Action Plan & Support */}
                  <Card className="border-l-4 border-l-heritage-blue">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="h-5 w-5 text-heritage-blue" />
                        3. Action Plan, Development & Support
                      </CardTitle>
                      <p className="text-sm text-gray-600">Add specific actions, development needs, and support required</p>
                    </CardHeader>
                    <CardContent>
                      <DiscussionItems
                        points={getCurrentKPAPoints('actions')}
                        onAddPoint={(content) => session.handleAddPoint(currentKPA.id, 'actions', content)}
                        onDeletePoint={(pointId) => session.handleDeletePoint(currentKPA.id, 'actions', pointId)}
                        onUpdatePoint={(pointId, content) => session.handleUpdatePoint(currentKPA.id, 'actions', pointId, content)}
                        currentUserRole={userRole}
                        canEdit={!isReviewCompleted}
                        placeholder="E.g., Enrol in advanced project management certification by Q2..."
                        maxLength={500}
                      />
                    </CardContent>
                  </Card>
              </div>

              {/* PPTX gap — People Leader Observations now live in their own
                  dedicated "Leader Observations" tab (added in the May 2026
                  rework) rather than being embedded at the bottom of the 1:1
                  dialogue. HR feedback: "not sure where the buttons are" — a
                  top-level tab makes the add affordance discoverable. The
                  pre-check-in observations on MemberKPAPanel are unaffected. */}

              {/* KPA Rating Section (for formal reviews only).
                  #34 / #22: the "Impact & Growth Indicator / Current Rating"
                  card is a rating surface — not a team-member concern in the
                  rework, and it undermines the calm Simple layout. Hide it for
                  the team member in Simple view; the People Leader always sees
                  it (they own the rating), and the team member can still see
                  the People Leader's rating read-only in Detailed view. */}
              {isFormalReview && !(isTeamMember && isSimpleView) && (
                <Card className="border-l-4 border-l-indigo-500 bg-gradient-to-br from-indigo-50/50 to-white" data-tour="kpa-rating">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Award className="h-6 w-6 text-indigo-600" />
                      KPA Impact & Growth Indicator
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-2">
                      Based on the portfolio entries and discussion above, provide the impact & growth indicator for this KPA
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {currentKPARating && (
                      <div className="mb-2 p-4 bg-white rounded-xl border-2 border-indigo-200 shadow-sm">
                        <p className="text-sm font-medium text-gray-700 mb-2">Current Impact & Growth Indicator:</p>
                        <Badge className={`${getRatingColor(currentKPARating)} border-2 text-lg px-4 py-1`}>
                          {getRatingLabel(currentKPARating)}
                        </Badge>
                      </div>
                    )}

                    {/* Rating selection (PL) / read-only manager rating (team
                        member). Hidden on a completed review — the locked-in
                        rating already shows in the "Current Rating" badge above. */}
                    {!isReviewCompleted && session.tooSoonToRate && canRate && (
                      <p className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700" data-testid="tstr-note">
                        {memberName.split(' ')[0]} started on {new Date(`${session.tooSoonToRate.first_hire}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })},
                        less than three months before the check-ins end, so this check-in is rated Too Soon To Rate.
                        You still have the check-in conversation.
                      </p>
                    )}
                    {!isReviewCompleted && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {canRate ? (
                        <>
                          {/* Too Soon To Rate (HR UAT list #20): only TSTR can be chosen. */}
                          {!session.tooSoonToRate && (<>
                          <button
                            type="button"
                            className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                              currentKPARating === 'exceptional'
                                ? 'bg-gradient-to-br from-purple-500 to-purple-600 border-purple-600 shadow-xl transform scale-[1.02]'
                                : 'bg-white border-purple-200 hover:border-purple-400 hover:shadow-lg hover:scale-[1.01]'
                            }`}
                            onClick={() => !isReviewCompleted && session.handleKPARating(currentKPA.id || currentKPA.name, 'exceptional')}
                            disabled={isReviewCompleted}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <Award className={`h-5 w-5 ${currentKPARating === 'exceptional' ? 'text-white' : 'text-purple-600'}`} />
                              <span className={`font-bold text-lg ${currentKPARating === 'exceptional' ? 'text-white' : 'text-purple-900'}`}>
                                Exceptional
                              </span>
                            </div>
                            <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                              currentKPARating === 'exceptional' ? 'text-white/95' : 'text-gray-700'
                            }`}>
                              Delivers significantly more work than required - much bigger than their jobs. Significantly exceeding challenging goals and targets on all KPAs
                            </p>
                          </button>

                          <button
                            type="button"
                            className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                              currentKPARating === 'full'
                                ? 'bg-gradient-to-br from-green-500 to-green-600 border-green-600 shadow-xl transform scale-[1.02]'
                                : 'bg-white border-green-200 hover:border-green-400 hover:shadow-lg hover:scale-[1.01]'
                            }`}
                            onClick={() => !isReviewCompleted && session.handleKPARating(currentKPA.id || currentKPA.name, 'full')}
                            disabled={isReviewCompleted}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <CheckCircle className={`h-5 w-5 ${ currentKPARating === 'full' ? 'text-white' : 'text-green-600'}`} />
                              <span className={`font-bold text-lg ${currentKPARating === 'full' ? 'text-white' : 'text-green-900'}`}>
                                Full
                              </span>
                            </div>
                            <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                              currentKPARating === 'full' ? 'text-white/95' : 'text-gray-700'
                            }`}>
                              Gets all the work done. Consistently achieving all mission critical targets and standards over all KPAs
                            </p>
                          </button>

                          <button
                            type="button"
                            className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                              currentKPARating === 'not-yet-full'
                                ? 'bg-gradient-to-br from-amber-500 to-amber-600 border-amber-600 shadow-xl transform scale-[1.02]'
                                : 'bg-white border-amber-200 hover:border-amber-400 hover:shadow-lg hover:scale-[1.01]'
                            }`}
                            onClick={() => !isReviewCompleted && session.handleKPARating(currentKPA.id || currentKPA.name, 'not-yet-full')}
                            disabled={isReviewCompleted}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <TrendingUp className={`h-5 w-5 ${currentKPARating === 'not-yet-full' ? 'text-white' : 'text-amber-600'}`} />
                              <span className={`font-bold text-lg ${currentKPARating === 'not-yet-full' ? 'text-white' : 'text-amber-900'}`}>
                                Not Yet Full
                              </span>
                            </div>
                            <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                              currentKPARating === 'not-yet-full' ? 'text-white/95' : 'text-gray-700'
                            }`}>
                              Does not yet get all the work done – however, real improvement is evident all around (may include exceptional performers in new challenging positions)
                            </p>
                          </button>

                          <button
                            type="button"
                            className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                              currentKPARating === 'weak'
                                ? 'bg-gradient-to-br from-red-500 to-red-600 border-red-600 shadow-xl transform scale-[1.02]'
                                : 'bg-white border-red-200 hover:border-red-400 hover:shadow-lg hover:scale-[1.01]'
                            }`}
                            onClick={() => !isReviewCompleted && session.handleKPARating(currentKPA.id || currentKPA.name, 'weak')}
                            disabled={isReviewCompleted}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <AlertTriangle className={`h-5 w-5 ${currentKPARating === 'weak' ? 'text-white' : 'text-red-600'}`} />
                              <span className={`font-bold text-lg ${currentKPARating === 'weak' ? 'text-white' : 'text-red-900'}`}>
                                Weak
                              </span>
                            </div>
                            <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                              currentKPARating === 'weak' ? 'text-white/95' : 'text-gray-700'
                            }`}>
                              Unable to get the work done - not showing signs of real improvement. Immediate and significant improvement in impact & growth is needed
                            </p>
                          </button>
                          </>)}

                          {/* TSTR — employees with under 3 months Eduvos
                              tenure cannot meaningfully be rated on this KPA.
                              Spans the grid row; excluded from analytics. */}
                          <button
                            type="button"
                            className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left sm:col-span-2 ${
                              currentKPARating === 'tstr'
                                ? 'bg-gradient-to-br from-slate-500 to-slate-600 border-slate-600 shadow-xl transform scale-[1.01]'
                                : 'bg-white border-slate-200 hover:border-slate-400 hover:shadow-lg hover:scale-[1.005]'
                            }`}
                            onClick={() => !isReviewCompleted && session.handleKPARating(currentKPA.id || currentKPA.name, 'tstr')}
                            disabled={isReviewCompleted}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <Clock className={`h-5 w-5 ${currentKPARating === 'tstr' ? 'text-white' : 'text-slate-600'}`} />
                              <span className={`font-bold text-lg ${currentKPARating === 'tstr' ? 'text-white' : 'text-slate-900'}`}>
                                Too Soon to Rate (TSTR)
                              </span>
                            </div>
                            <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                              currentKPARating === 'tstr' ? 'text-white/95' : 'text-gray-700'
                            }`}>
                              Team member has under 3 months tenure at Eduvos — insufficient time in role for a meaningful assessment of this KPA.
                            </p>
                          </button>
                        </>
                      ) : (
                        <div className="sm:col-span-2">
                          <Card className="border-l-4 border-l-heritage-blue">
                            <CardContent className="p-6">
                              <div className="flex items-start gap-4">
                                <Award className="h-8 w-8 text-heritage-blue mt-1" />
                                <div className="flex-1">
                                  <h4 className="font-semibold text-lg mb-2">People Leader Impact & Growth Indicator for this KPA</h4>
                                  <Badge className={`${getRatingColor(currentKPARating)} border-2 text-lg px-4 py-2 mb-3`}>
                                    {getRatingLabel(currentKPARating)}
                                  </Badge>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                    </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Tier 3: AI Rating Assistant */}
              {isFormalReview && canRate && (
                <RatingAssistant
                  rationale={ai.ratingRationale[currentKPA.id] || null}
                  isLoading={ai.loadingRationaleFor === currentKPA.id}
                  onGenerateRationale={() => ai.loadRatingRationale(currentKPA.id, activeKpaData, kpaDiscussionPoints, kpaRatings)}
                  currentRating={currentKPARating || null}
                  onAcceptSuggestion={(rating) => ai.handleAcceptRatingSuggestion(currentKPA.id || currentKPA.name, rating, session.setKpaRatings)}
                />
              )}
            </TabsContent>

            {/* Evidence Portfolio Tab. Sits at the shared wrapper's px-6 inset
                like every other tab — EvidencePortfolio (impact mode) adds no
                root side padding of its own, so no -mx-6 cancel is needed. */}
            <TabsContent value="evidence" className="m-0">
              {onAddEvidence && onDeleteEvidence && onUploadDocument ? (
                <EvidencePortfolio
                  evidence={currentKPAEvidence}
                  userRole={userRole}
                  // Impact Portfolio is always the calm simple view — it's a
                  // judgement-free evidence space, not a metrics surface, so it
                  // ignores the global "Show details" toggle.
                  viewMode="simple"
                  // When the employee opens their own review, currentUserId must be
                  // their own personnel number (memberId), not the manager's id.
                  currentUserId={userRole === 'employee' ? memberId : managerId}
                  targetUserId={memberId}
                  kpaId={currentKPA.id}
                  availableKPIs={availableKPIs}
                  hideUploadButton={true}
                  readOnly={isReviewCompleted}
                  reviewPeriod={reviewPeriod}
                  onAddEvidence={isReviewCompleted ? noOpEvidenceAdd as any : onAddEvidence}
                  onUpdateEvidence={isReviewCompleted ? noOpEvidenceUpdate as any : onUpdateEvidence}
                  onDeleteEvidence={isReviewCompleted ? noOpEvidenceDelete as any : onDeleteEvidence}
                  onUploadDocument={isReviewCompleted ? noOpDocumentUpload as any : onUploadDocument}
                  onClassifyEvidence={isReviewCompleted ? noOpClassify as any : onUpdateEvidenceClassification}
                  onRefreshEvidence={onRefreshEvidence}
                  onReleaseEvidence={canReleaseHere ? handleReleaseEvidence : undefined}
                  onWithdrawEvidence={canReleaseHere ? handleWithdrawEvidence : undefined}
                />
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-heritage-blue" />
                      Impact Portfolio for {currentKPA.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Visibility is enforced server-side now (release-gated:
                          released narratives + all measurements + this PL's own
                          observations), so no client-side visibility filter. */}
                      {currentKPAEvidence.length > 0 ? (
                        currentKPAEvidence.map((item) => (
                          <Card key={item.id} className="border-l-4 border-l-purple-600">
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium">{item.title}</h4>
                                  <p className="text-sm text-gray-600">{item.type} • {item.date}</p>
                                </div>
                                <Badge className={`${
                                  item.classification === 'exceptional' ? 'bg-purple-100 text-purple-800' :
                                  item.classification === 'full' ? 'bg-green-100 text-green-800' :
                                  item.classification === 'not-yet-full' ? 'bg-amber-100 text-amber-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {item.classification.replace('-', ' ')}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <p className="text-gray-600 text-center py-12">
                          No entries submitted for this KPA yet
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </div>
        </Tabs>
        )}

        {/* Feedback View. No fallback noop on onCreateFeedbackRequest: when
            the parent supplies no handler (employee "My reflection" via
            ReviewsTab), ReviewFeedbackView runs its own dialog + submit. The
            old console.log fallback swallowed the employee's Request
            Feedback click entirely. */}
        {viewMode === 'feedback' && (
          <ReviewFeedbackView
            userRole={userRole}
            memberId={memberId}
            memberFeedbackRequests={memberFeedbackRequests}
            loadingFeedback={loadingFeedback}
            onCreateFeedbackRequest={onCreateFeedbackRequest}
            onRefreshFeedback={handleRefreshFeedback}
            canRate={canRate}
            isReviewCompleted={isReviewCompleted}
            isFormalReview={isFormalReview}
            allKPAsRated={allKPAsRated}
            setViewMode={setViewMode}
          />
        )}

        {viewMode === 'next-year-objectives' && showNextYearStep && (
          <NextYearObjectivesStep
            memberId={memberId}
            memberName={memberName}
            memberRole={memberRole}
            reviewYear={reviewYear!}
            userRole={userRole}
          />
        )}

        {/* Impact Portfolio View — full released set for THIS check-in. */}
        {viewMode === 'impact-portfolio' && (
          <div ref={contentScrollRef} className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Impact Portfolio</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {isTeamMember
                  ? 'Your portfolio for this check-in — across all areas, plus general entries. Share the entries you want to discuss.'
                  : `Everything ${memberName?.split(' ')[0] || 'this team member'} shared for this check-in — across all areas, plus general entries.`}
              </p>
            </div>
            <EvidencePortfolio
              evidence={releasedSessionEvidence}
              kpaId={undefined}
              kpaOptions={portfolioKpaOptions}
              availableKPIs={portfolioAvailableKPIs}
              userRole={userRole}
              // Impact Portfolio is always the calm simple view (ignores the
              // global "Show details" toggle).
              viewMode="simple"
              currentUserId={userRole === 'employee' ? memberId : managerId}
              targetUserId={memberId}
              reviewPeriod={reviewPeriod}
              skipPeriodDisplayFilter={true}
              showManagerCannotAddNotice={userRole === 'manager'}
              readOnly={isReviewCompleted}
              ownerDisplayName={memberName}
              onAddEvidence={isReviewCompleted ? noOpEvidenceAdd as any : onAddEvidence}
              onUpdateEvidence={isReviewCompleted ? noOpEvidenceUpdate as any : onUpdateEvidence}
              onDeleteEvidence={isReviewCompleted ? noOpEvidenceDelete as any : onDeleteEvidence}
              onUploadDocument={isReviewCompleted ? noOpDocumentUpload as any : onUploadDocument}
              onClassifyEvidence={isReviewCompleted ? noOpClassify as any : onUpdateEvidenceClassification}
              onRefreshEvidence={onRefreshEvidence}
              onReleaseEvidence={canReleaseHere ? handleReleaseEvidence : undefined}
              onWithdrawEvidence={canReleaseHere ? handleWithdrawEvidence : undefined}
            />
          </div>
        )}

        {/* Leader Observations View — the member card's panel; read-only once the
            check-in is completed, like the rest of a completed check-in. */}
        {viewMode === 'leader-observations' && showLeaderObservationsTab && (
          <div ref={contentScrollRef} className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-6">
            <LeaderObservationsPanel
              memberName={memberName}
              evidence={evidence}
              kpaOptions={portfolioKpaOptions}
              currentUserId={managerId}
              targetUserId={memberId}
              reviewPeriod={reviewPeriod}
              readOnly={isReviewCompleted}
              viewMode={journeyViewMode}
              onAddEvidence={isReviewCompleted ? noOpEvidenceAdd : onAddEvidence}
              onUpdateEvidence={isReviewCompleted ? noOpEvidenceUpdate : onUpdateEvidence}
              onDeleteEvidence={isReviewCompleted ? noOpEvidenceDelete : onDeleteEvidence}
              onUploadDocument={isReviewCompleted ? noOpDocumentUpload : onUploadDocument}
              onClassifyEvidence={isReviewCompleted ? noOpClassify : onUpdateEvidenceClassification}
              onRefreshEvidence={onRefreshEvidence}
            />
          </div>
        )}

        {/* Finalise View */}
        {viewMode === 'finalize' && (canRate || isReviewCompleted) && (
          <ReviewFinalizeView
            viewerRole={userRole}
            memberName={memberName}
            isFormalReview={isFormalReview}
            isReviewCompleted={isReviewCompleted}
            canRate={canRate}
            tooSoonToRate={session.tooSoonToRate}
            recordedByHr={session.recordedByHr}
            calibrationRecordedByHr={session.calibrationRecordedByHr}
            allKPAsRated={allKPAsRated}
            kpaData={kpaData}
            kpaRatings={kpaRatings}
            overallRating={overallRating}
            setOverallRating={session.setOverallRating}
            ratingJustification={ratingJustification}
            setRatingJustification={session.setRatingJustification}
            reviewSynthesis={ai.reviewSynthesis}
            loadingSynthesis={ai.loadingSynthesis}
            onGenerateSynthesis={() => ai.loadReviewSynthesis(activeKpaData, kpaRatings, overallRating, kpaDiscussionPoints, managerComments, ratingJustification)}
            onExportPDF={ai.handleExportPDF}
            onDeleteSynthesis={ai.handleDeleteSynthesis}
            onSaveSynthesis={ai.saveEditedSynthesis}
            kpaDiscussionPoints={kpaDiscussionPoints}
            discussedKpaData={activeKpaData}
            memberIDP={idp.memberIDP}
            loadingIDP={idp.loadingIDP}
            prepareIDPTransferItems={idp.prepareIDPTransferItems}
            showIDPTransfer={idp.showIDPTransfer}
            setShowIDPTransfer={idp.setShowIDPTransfer}
            idpTransferItems={idp.idpTransferItems}
            transferringToIDP={idp.transferringToIDP}
            toggleIDPTransferItem={idp.toggleIDPTransferItem}
            handleTransferToIDP={idp.handleTransferToIDP}
            onCompleteReview={session.handleCompleteReview}
            onConfirmCompleteOpen={() => session.setConfirmCompleteOpen(true)}
            setViewMode={setViewMode}
            getReviewTypeLabel={getReviewTypeLabel}
          />
        )}

        {/* Shared step footer — pinned at the bottom for the views that don't
            carry their own (headlines / KPA / Impact Portfolio). Back/Next walk
            the top-level tab sequence so the whole check-in reads as one guided
            flow. Feedback & Finalise keep their own footers (they own the primary
            actions like "Go to Finalise" / "Complete"). */}
        {(viewMode === 'self-reflection' || viewMode === 'kpa' || viewMode === 'impact-portfolio' || viewMode === 'leader-observations') && (
          <div className="border-t bg-gray-50 p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0">
            {/* Left: Close */}
            <div className="flex items-center gap-2 w-full md:w-48">
              <Button variant="outline" onClick={onClose} className="w-full md:w-auto">
                Close
              </Button>
            </div>

            {/* Center: top-level step navigation across the tabs. */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button variant="outline" onClick={goToPrevStep} disabled={isFirstStep} className="min-w-[100px]">
                Back
              </Button>

              {/* #22: PL-only KPA-ratings counter (Detailed view), on the KPA step. */}
              {viewMode === 'kpa' && isFormalReview && !isTeamMember && !isSimpleView && (
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-heritage-blue/20 shadow-sm">
                  <Award className="h-4 w-4 text-heritage-blue" />
                  <span className="text-sm font-semibold text-gray-700">
                    KPA Impact & Growth Indicators: <span className="text-heritage-blue">{ratedKPAsCount}</span> / {kpaData.length}
                  </span>
                </div>
              )}

              {!isLastStep && (
                <Button onClick={goToNextStep} className="bg-heritage-blue min-w-[140px]">
                  {nextStepLabel}
                </Button>
              )}
            </div>

            {/* Right spacer — balances the left Close so the center stays centred. */}
            <div className="hidden md:block md:w-48" />
          </div>
        )}
      </div>

      {/* KPI-Specific Evidence Modal */}
      <Dialog open={evidenceModalOpen} onOpenChange={setEvidenceModalOpen}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Impact Portfolio for {selectedKPI?.name}</DialogTitle>
            <DialogDescription>
              {selectedKPI && (
                <>View and manage entries for this KPI. Entries will be automatically linked to <strong>{selectedKPI.name}</strong>.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {((onAddEvidence && onDeleteEvidence && onUploadDocument) || readOnly) && selectedKPI ? (
              <EvidencePortfolio
                evidence={evidence.filter(e => e.kpiId === selectedKPI.id)}
                kpaId={currentKPA?.id}
                availableKPIs={selectedKPI ? [{
                  id: selectedKPI.id,
                  name: selectedKPI.name,
                  objectiveTitle: currentKPA?.individualObjectives?.find((obj: any) => obj.id === selectedKPI.objectiveId)?.title,
                  current_value: currentKPA?.underlyingKPIs?.find((k: any) => k.id === selectedKPI.id)?.current_value ?? undefined,
                  target_value: currentKPA?.underlyingKPIs?.find((k: any) => k.id === selectedKPI.id)?.target_value,
                  unit: currentKPA?.underlyingKPIs?.find((k: any) => k.id === selectedKPI.id)?.unit,
                  measurement_history: (currentKPA?.underlyingKPIs?.find((k: any) => k.id === selectedKPI.id) as any)?.measurement_history,
                  midyear_value: (currentKPA?.underlyingKPIs?.find((k: any) => k.id === selectedKPI.id) as any)?.midyear_value,
                  yearend_value: (currentKPA?.underlyingKPIs?.find((k: any) => k.id === selectedKPI.id) as any)?.yearend_value
                }] : availableKPIs}
                preSelectedKPIId={selectedKPI.id}
                hideUploadButton={true}
                readOnly={isReviewCompleted || readOnly}
                reviewPeriod={reviewPeriod}
                userRole={userRole}
                // Impact Portfolio is always the calm simple view (ignores the
                // global "Show details" toggle).
                viewMode="simple"
                // When the employee opens their own review, currentUserId must be
                // their own personnel number (memberId), not the manager's id.
                currentUserId={userRole === 'employee' ? memberId : managerId}
                targetUserId={memberId}
                onAddEvidence={(isReviewCompleted || readOnly) ? noOpEvidenceAdd as any : handleAddKPISpecificEvidence}
                onUpdateEvidence={(isReviewCompleted || readOnly) ? noOpEvidenceUpdate as any : onUpdateEvidence}
                onDeleteEvidence={(isReviewCompleted || readOnly) ? noOpEvidenceDelete as any : onDeleteEvidence}
                onUploadDocument={(isReviewCompleted || readOnly) ? noOpDocumentUpload as any : onUploadDocument}
                onClassifyEvidence={(isReviewCompleted || readOnly) ? noOpClassify as any : onUpdateEvidenceClassification}
                onRefreshEvidence={onRefreshEvidence}
              />
            ) : (
              <div className="p-8 text-center text-gray-500">Impact Portfolio handlers not available</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Confirm Complete Check-in Dialog */}
      <Dialog open={session.confirmCompleteOpen} onOpenChange={session.setConfirmCompleteOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Complete {getReviewTypeLabel()}?
            </DialogTitle>
            <DialogDescription>
              This will mark the check-in for <strong>{memberName}</strong> as completed.
              All discussion points will be saved.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => session.setConfirmCompleteOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                session.setConfirmCompleteOpen(false)
                session.handleCompleteInformalReview(currentKPA)
              }}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete Check-in
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Confirm Reopen Review Dialog (admin / hr_admin escape-hatch) */}
      <Dialog open={confirmReopenOpen} onOpenChange={setConfirmReopenOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-heritage-blue" />
              Reopen this completed review?
            </DialogTitle>
            <DialogDescription>
              Reopen this completed review? It returns to In-Progress so items can be
              added and it must be re-finalised.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setConfirmReopenOpen(false)} disabled={isReopening}>
              Cancel
            </Button>
            <Button
              className="bg-heritage-blue"
              onClick={handleReopenReview}
              disabled={isReopening}
            >
              {isReopening ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Reopen review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>,
    document.body
  )
}
